/**
 * Replay Setup Panel - Time Range Selection
 * Allows users to select a time window for replay mode
 */
export class ReplaySetup {
  constructor(httpUrl) {
    this.panel = null;
    this.isVisible = false;
    this.selectedTask = null;
    this.tasks = [];
    this.pilotCounts = new Map(); // Map<taskId, pilotCount>
    this.timeRange = {
      start: null,
      end: null
    };
    this.httpUrl = httpUrl;
    
    this.init();
  }

  /**
   * Initialize the replay setup panel
   */
  init() {
    console.log('🎬 ReplaySetup: Initializing...');
    this.createPanel();
    this.setupEventListeners();
  }

  /**
   * Create the setup panel DOM structure
   */
  createPanel() {
    // Create overlay
    const overlay = document.createElement('div');
    overlay.className = 'replay-setup-overlay';
    overlay.id = 'replay-setup-overlay';

    // Create panel
    const panel = document.createElement('div');
    panel.className = 'replay-setup-panel';
    panel.id = 'replay-setup-panel';
    
    panel.innerHTML = `
      <div class="replay-setup-header">
        <h2 class="replay-setup-title">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8"/>
            <path d="M21 3v5h-5"/>
            <path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16"/>
            <path d="M8 16H3v5"/>
          </svg>
          Setup Replay
        </h2>
        <button class="replay-setup-close" id="replay-setup-close">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <line x1="18" y1="6" x2="6" y2="18"/>
            <line x1="6" y1="6" x2="18" y2="18"/>
          </svg>
        </button>
      </div>

      <div class="replay-setup-content">
        <!-- Task Selection -->
        <div class="replay-setup-section">
          <h3 class="replay-setup-section-title">Select Task</h3>
          <p class="replay-setup-description">Choose a task to replay its flight data</p>
          
          <select class="replay-task-select" id="replay-task-select">
            <option value="">-- Loading tasks... --</option>
          </select>
        </div>

        <!-- Time Range Selection -->
        <div class="replay-setup-section">
          <h3 class="replay-setup-section-title">Time Range</h3>
          <p class="replay-setup-description">Adjust the time window to replay</p>
          
          <div class="replay-time-inputs">
            <div class="replay-time-input-group">
              <label for="replay-start-time">Start Time</label>
              <input type="datetime-local" id="replay-start-time" class="replay-time-input" />
            </div>
            <div class="replay-time-input-group">
              <label for="replay-end-time">End Time</label>
              <input type="datetime-local" id="replay-end-time" class="replay-time-input" />
            </div>
          </div>

          <!-- Visual Timeline Preview -->
          <div class="replay-timeline-preview">
            <div class="timeline-bar">
              <div class="timeline-track"></div>
              <div class="timeline-markers-container" id="timeline-markers-container">
                <!-- Task markers will be added dynamically -->
              </div>
            </div>
            <div class="timeline-labels">
              <span class="timeline-label-start" id="timeline-label-start">--:--</span>
              <span class="timeline-label-end" id="timeline-label-end">--:--</span>
            </div>
          </div>
        </div>

        <!-- Data Info -->
        <div class="replay-setup-section replay-data-info" id="replay-data-info">
          <div class="replay-info-item">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M17.8 19.2 16 11l3.5-3.5C21 6 21.5 4 21 3c-1-.5-3 0-4.5 1.5L13 8 4.8 6.2c-.5-.1-.9.1-1.1.5l-.3.5c-.2.5-.1 1 .3 1.3L9 12l-2 3H4l-1 1 3 2 2 3 1-1v-3l3-2 3.5 5.3c.3.4.8.5 1.3.3l.5-.2c.4-.3.6-.7.5-1.2z"/>
            </svg>
            <span id="replay-glider-count">Gliders: <strong>--</strong></span>
          </div>
        </div>
      </div>

      <div class="replay-setup-footer">
        <button class="replay-setup-btn replay-setup-cancel" id="replay-setup-cancel">
          Cancel
        </button>
        <button class="replay-setup-btn replay-setup-load" id="replay-setup-load">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <polygon points="5 3 19 12 5 21 5 3" fill="currentColor"/>
          </svg>
          Load Replay
        </button>
      </div>
    `;

    overlay.appendChild(panel);
    document.body.appendChild(overlay);
    
    this.panel = panel;
    this.overlay = overlay;
  }

  /**
   * Setup event listeners
   */
  setupEventListeners() {
    // Close button
    const closeBtn = document.getElementById('replay-setup-close');
    if (closeBtn) {
      closeBtn.addEventListener('click', () => this.hide());
    }

    // Cancel button
    const cancelBtn = document.getElementById('replay-setup-cancel');
    if (cancelBtn) {
      cancelBtn.addEventListener('click', () => this.hide());
    }

    // Load button
    const loadBtn = document.getElementById('replay-setup-load');
    if (loadBtn) {
      loadBtn.addEventListener('click', () => this.loadReplay());
    }

    // Close on overlay click
    this.overlay.addEventListener('click', (e) => {
      if (e.target === this.overlay) {
        this.hide();
      }
    });

    // Task selection
    const taskSelect = document.getElementById('replay-task-select');
    if (taskSelect) {
      taskSelect.addEventListener('change', (e) => this.onTaskSelected(e.target.value));
    }

    // ESC key to close
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isVisible) {
        this.hide();
      }
    });
  }

  /**
   * Show the setup panel
   */
  show() {
    this.isVisible = true;
    this.overlay.classList.add('visible');
    console.log('🎬 ReplaySetup: Panel shown');
    
    // Disable Load button initially (no task selected)
    this.updateLoadButtonState();
    
    // Load tasks when panel is shown
    this.loadTasks();
    
    this.emitEvent('shown');
  }

  /**
   * Hide the setup panel
   */
  hide() {
    this.isVisible = false;
    this.overlay.classList.remove('visible');
    console.log('🎬 ReplaySetup: Panel hidden');
    this.emitEvent('hidden');
  }

  /**
   * Load tasks from API (past 30 days only, up to today)
   */
  async loadTasks() {
    const taskSelect = document.getElementById('replay-task-select');
    if (!taskSelect) return;
    
    try {
      // Get tasks from last 30 days up to NOW (no future tasks)
      const now = new Date();
      const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
      const startTimeFilter = thirtyDaysAgo.toISOString();
      const endTimeFilter = now.toISOString(); // Only up to now
      
      const response = await fetch(`${this.httpUrl}/api/tasks?startTime=${startTimeFilter}&endTime=${endTimeFilter}`);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      this.tasks = data.tasks || [];
      
      console.log(`🎬 ReplaySetup: Loaded ${this.tasks.length} tasks`);
      
      // Fetch pilot counts for all tasks
      await this.loadPilotCounts();
      
      // Populate dropdown
      if (this.tasks.length === 0) {
        taskSelect.innerHTML = '<option value="">-- No tasks available --</option>';
      } else {
        taskSelect.innerHTML = '<option value="">-- Select a Task --</option>';
        this.tasks.forEach(task => {
          const option = document.createElement('option');
          option.value = task.id;
          // API fields: task_name, associated_servers (array), ds_helper_start_time
          const serverName = task.associated_servers && task.associated_servers.length > 0 
            ? task.associated_servers[0] 
            : 'Unknown Server';
          option.textContent = `${task.task_name} - ${serverName} - ${this.formatTaskTime(task.ds_helper_start_time)}`;
          taskSelect.appendChild(option);
        });
      }
      
      // Plot tasks on timeline
      this.plotTasksOnTimeline();
      
    } catch (error) {
      console.error('❌ ReplaySetup: Failed to load tasks:', error);
      taskSelect.innerHTML = '<option value="">-- Failed to load tasks --</option>';
    }
  }

  /**
   * Plot tasks on timeline as green dots
   */
  plotTasksOnTimeline() {
    const markersContainer = document.getElementById('timeline-markers-container');
    const labelStart = document.getElementById('timeline-label-start');
    const labelEnd = document.getElementById('timeline-label-end');
    
    if (!markersContainer || this.tasks.length === 0) return;
    
    // Clear existing markers
    markersContainer.innerHTML = '';
    
    // Find time range across all tasks
    const taskTimes = this.tasks.map(t => new Date(t.ds_helper_start_time).getTime());
    const minTime = Math.min(...taskTimes);
    // Cap max time at NOW (don't show future)
    const maxTime = Math.min(Math.max(...taskTimes), Date.now());
    const timeRange = maxTime - minTime;
    
    // Add intermediate date markers (weekly)
    this.addDateMarkers(markersContainer, minTime, maxTime, timeRange);
    
    // Update timeline labels
    if (labelStart && labelEnd) {
      labelStart.textContent = this.formatTimeLabel(new Date(minTime));
      labelEnd.textContent = this.formatTimeLabel(new Date(maxTime));
    }
    
    // Plot each task as a dot (green if has pilots, gray if none)
    this.tasks.forEach(task => {
      const taskTime = new Date(task.ds_helper_start_time).getTime();
      const position = timeRange > 0 ? ((taskTime - minTime) / timeRange) * 100 : 50;
      
      // Check pilot count
      const pilotCount = this.pilotCounts.get(task.id) || 0;
      const hasNoPilots = pilotCount === 0;
      
      const marker = document.createElement('div');
      marker.className = `timeline-marker task-marker ${hasNoPilots ? 'no-pilots' : ''}`;
      marker.style.left = `${position}%`;
      const serverName = task.associated_servers && task.associated_servers.length > 0 
        ? task.associated_servers[0] 
        : 'Unknown Server';
      marker.title = `${task.task_name} - ${serverName}\n${this.formatTaskTime(task.ds_helper_start_time)}\nPilots: ${pilotCount}`;
      marker.dataset.taskId = task.id;
      
      marker.innerHTML = `
        <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
          <circle cx="12" cy="12" r="10"/>
        </svg>
      `;
      
      // Click to select task
      marker.addEventListener('click', () => {
        // Remove selected class from all markers
        document.querySelectorAll('.timeline-marker.task-marker').forEach(m => {
          m.classList.remove('selected');
        });
        
        // Add selected class to this marker
        marker.classList.add('selected');
        
        const taskSelect = document.getElementById('replay-task-select');
        if (taskSelect) {
          taskSelect.value = task.id;
          this.onTaskSelected(task.id);
        }
      });
      
      markersContainer.appendChild(marker);
    });
    
    console.log(`🎬 ReplaySetup: Plotted ${this.tasks.length} tasks on timeline`);
  }

  /**
   * Format task time for display
   */
  formatTaskTime(dateString) {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  /**
   * Load pilot counts for all tasks
   */
  async loadPilotCounts() {
    if (this.tasks.length === 0) return;
    
    try {
      const taskIds = this.tasks.map(t => t.id).join(',');
      const response = await fetch(`${this.httpUrl}/api/tasks/pilot-counts?taskIds=${taskIds}`);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      
      if (data.success && data.counts) {
        data.counts.forEach(item => {
          this.pilotCounts.set(item.task_id, item.pilot_count);
        });
        console.log(`🎬 ReplaySetup: Loaded pilot counts for ${data.counts.length} tasks`);
      }
    } catch (error) {
      console.error('❌ ReplaySetup: Failed to load pilot counts:', error);
    }
  }

  /**
   * Update pilot count display
   */
  updatePilotCountDisplay(taskId) {
    const gliderCountEl = document.getElementById('replay-glider-count');
    if (!gliderCountEl) return;
    
    const count = this.pilotCounts.get(parseInt(taskId)) || 0;
    
    if (count === 0 || count === null) {
      gliderCountEl.innerHTML = `<strong style="color: #ef4444;">No Gliders</strong>`;
    } else {
      gliderCountEl.innerHTML = `<strong style="color: #22c55e;">${count} Glider${count === 1 ? '' : 's'}</strong>`;
    }
  }

  /**
   * Update Load button state (disabled if no task selected)
   */
  updateLoadButtonState() {
    const loadBtn = document.getElementById('replay-setup-load');
    if (!loadBtn) return;
    
    if (this.selectedTask) {
      loadBtn.disabled = false;
      loadBtn.classList.remove('disabled');
    } else {
      loadBtn.disabled = true;
      loadBtn.classList.add('disabled');
    }
  }

  /**
   * Add date markers to timeline (weekly intervals)
   */
  addDateMarkers(container, minTime, maxTime, timeRange) {
    if (timeRange === 0) return;
    
    // Calculate number of days in range
    const days = timeRange / (1000 * 60 * 60 * 24);
    
    // Determine interval based on range
    let intervalDays;
    if (days <= 7) {
      intervalDays = 1; // Daily for week or less
    } else if (days <= 14) {
      intervalDays = 2; // Every 2 days
    } else if (days <= 21) {
      intervalDays = 3; // Every 3 days
    } else {
      intervalDays = 7; // Weekly for month
    }
    
    // Start from the first day
    const startDate = new Date(minTime);
    startDate.setHours(0, 0, 0, 0);
    
    // Add markers at intervals
    let currentDate = new Date(startDate);
    while (currentDate.getTime() <= maxTime) {
      const position = ((currentDate.getTime() - minTime) / timeRange) * 100;
      
      // Only add if within bounds
      if (position >= 0 && position <= 100) {
        const marker = document.createElement('div');
        marker.className = 'timeline-date-marker';
        marker.style.left = `${position}%`;
        marker.innerHTML = `
          <div class="date-marker-line"></div>
          <div class="date-marker-label">${this.formatTimeLabel(currentDate)}</div>
        `;
        container.appendChild(marker);
      }
      
      // Move to next interval
      currentDate.setDate(currentDate.getDate() + intervalDays);
    }
  }

  /**
   * Format time label for timeline (date only, no time)
   */
  formatTimeLabel(date) {
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric'
    });
  }

  /**
   * Handle task selection
   */
  onTaskSelected(taskId) {
    this.selectedTask = taskId;
    console.log('🎬 ReplaySetup: Task selected:', taskId);
    
    // Update Load button state
    this.updateLoadButtonState();
    
    // Find the selected task
    const task = this.tasks.find(t => t.id == taskId); // Use == for type coercion
    if (!task) {
      console.warn('Task not found:', taskId);
      return;
    }
    
    // Update pilot count display
    this.updatePilotCountDisplay(taskId);
    
    // Highlight the selected marker on timeline
    document.querySelectorAll('.timeline-marker.task-marker').forEach(marker => {
      if (marker.dataset.taskId == taskId) {
        marker.classList.add('selected');
      } else {
        marker.classList.remove('selected');
      }
    });
    
    // Set time range based on task
    const startTime = new Date(task.ds_helper_start_time);
    // Add 4 hours for end time (typical task duration)
    const endTime = new Date(startTime.getTime() + 4 * 60 * 60 * 1000);
    
    this.setTimeRange(startTime, endTime);
  }

  /**
   * Set time range
   */
  setTimeRange(start, end) {
    this.timeRange.start = start;
    this.timeRange.end = end;

    // Update input fields
    const startInput = document.getElementById('replay-start-time');
    const endInput = document.getElementById('replay-end-time');
    
    if (startInput && start) {
      startInput.value = this.formatDateTimeLocal(start);
    }
    if (endInput && end) {
      endInput.value = this.formatDateTimeLocal(end);
    }

    console.log('🎬 ReplaySetup: Time range set:', { start, end });
  }

  /**
   * Format date for datetime-local input
   */
  formatDateTimeLocal(date) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  }

  /**
   * Load replay with selected settings
   */
  loadReplay() {
    if (!this.selectedTask) {
      alert('Please select a task to replay');
      return;
    }

    const startInput = document.getElementById('replay-start-time');
    const endInput = document.getElementById('replay-end-time');
    
    const startTime = startInput ? new Date(startInput.value) : this.timeRange.start;
    const endTime = endInput ? new Date(endInput.value) : this.timeRange.end;

    if (!startTime || !endTime) {
      alert('Please select a valid time range');
      return;
    }

    // Always use JSON format (full telemetry)
    const format = 'json';

    console.log('🎬 ReplaySetup: Loading replay...', {
      task: this.selectedTask,
      start: startTime,
      end: endTime,
      format: format
    });

    // Emit event with replay configuration
    this.emitEvent('load', {
      task: this.selectedTask,
      format: format,
      timeRange: {
        start: startTime,
        end: endTime
      }
    });

    // Hide setup panel (loading modal will be shown by main.js)
    this.hide();
  }

  /**
   * Event emission system
   */
  emitEvent(eventName, data = {}) {
    const event = new CustomEvent(`replaySetup:${eventName}`, {
      detail: { ...data, setup: this }
    });
    document.dispatchEvent(event);
  }

  /**
   * Add event listener
   */
  addEventListener(eventName, callback) {
    document.addEventListener(`replaySetup:${eventName}`, callback);
  }
}
